"use client";

import clsx from "clsx";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import type {OmegaActivity} from "@/services/wingman/omega-results";

function activityIcon(activity: OmegaActivity, busy: boolean) {
   if (activity.error) return "triangle-exclamation";
   if (activity.pending && busy) return "spinner";
   return activity.pending ? "clock" : "check";
}

/** Conversation history, separate from the current saved workflow state. */
export default function WorkflowActivity({activities, busy}: {activities: OmegaActivity[]; busy: boolean}) {
   const {t} = useTranslation("ask");
   const latest = activities.at(-1);
   if (!latest) return null;
   const preceding = activities.slice(0, -1).reverse();
   const pending = preceding.filter(activity => activity.pending && busy);
   const earlier = preceding.filter(activity => !(activity.pending && busy));

   return (
      <details className="group/activity min-w-0 rounded-xl border border-300 bg-panel" open={busy}>
         <summary className="flex cursor-pointer list-none items-center gap-3 rounded-xl px-4 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
            <Icon icon="clock-rotate-left" className="h-4 w-4 shrink-0 text-500" />
            <span className="min-w-0 flex-1 font-medium text-700">{t("workflowPanel.activityCount", {count: activities.length})}</span>
            <Icon icon="chevron-right" className="h-3 w-3 shrink-0 text-500 transition-transform group-open/activity:rotate-90" />
         </summary>
         <div className="border-t border-300">
            <p className="px-4 py-3 text-xs leading-relaxed text-500">{t("workflowPanel.activityHint")}</p>
            <ul className="max-h-80 overflow-y-auto overscroll-contain px-2 pb-2">
               <WorkflowActivityRow key={latest.id} activity={latest} busy={busy} />
               {pending.map(activity => <WorkflowActivityRow key={activity.id} activity={activity} busy={busy} />)}
               {earlier.length > 0 && <li className="border-t border-300">
                  <details className="group/earlier">
                     <summary className="flex cursor-pointer list-none items-center gap-2.5 rounded-lg p-2 text-xs text-500 hover:bg-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                           <Icon icon="clock-rotate-left" className="h-3 w-3" />
                        </span>
                        <span className="min-w-0 flex-1">{t("workflowPanel.earlierAttempts", {count: earlier.length})}</span>
                        <Icon icon="chevron-right" className="h-3 w-3 shrink-0 transition-transform group-open/earlier:rotate-90" />
                     </summary>
                     <ul>
                        {earlier.map(activity => <WorkflowActivityRow key={activity.id} activity={activity} busy={busy} />)}
                     </ul>
                  </details>
               </li>}
            </ul>
         </div>
      </details>
   );
}

function WorkflowActivityRow({activity, busy}: {activity: OmegaActivity; busy: boolean}) {
   const {t} = useTranslation("ask");
   const [open, setOpen] = useState(false);
   const json = useMemo(() => open ? JSON.stringify(activity.detail, null, 2) : undefined, [open, activity.detail]);
   const pending = activity.pending && busy;
   const label = t(`workflowPanel.activity.${activity.label}`, activity.params) + (pending ? "…" : "");
   const heading = <>
      <span className={clsx("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
         activity.error ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300" : "bg-100 text-500"
      )}>
         <Icon icon={activityIcon(activity, busy)}
            className={clsx("h-3 w-3", pending && "motion-safe:animate-spin")} />
      </span>
      <span className="min-w-0 flex-1 break-words text-xs leading-5 text-700">{label}</span>
   </>;
   return <li className="border-t border-300 first:border-0">
      {activity.detail !== undefined ? (
         <details className="group/entry" onToggle={(event) => setOpen(event.currentTarget.open)}>
            <summary className="flex cursor-pointer list-none items-start gap-2.5 rounded-lg p-2 hover:bg-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
               {heading}
               <Icon icon="chevron-right" className="mt-1.5 h-3 w-3 shrink-0 text-500 transition-transform group-open/entry:rotate-90" />
            </summary>
            {open && <div className="mx-2 mb-3 min-w-0 rounded-lg border border-300 bg-100">
               <p className="border-b border-300 px-3 py-2 text-xs font-medium text-500">{t("workflowPanel.technicalDetails")}</p>
               {/* Keyboard users need to focus this scrollable region to read long responses. */}
               {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
               <section tabIndex={0} aria-label={t("workflowPanel.technicalDetails")} className="max-h-56 overflow-auto overscroll-contain whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed text-600 [overflow-wrap:anywhere]">
                  <pre className="whitespace-pre-wrap break-words font-mono">{json}</pre>
               </section>
            </div>}
         </details>
      ) : <div className="flex items-start gap-2.5 p-2">{heading}</div>}
   </li>;
}
